import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { FormatDate } from '../../utils/Tools';
import { Selectable } from '../../utils/CommonTypes/CommonTypes';
import Button from '../_Core/Button';
import Icon, { IconType } from '../Icon/Icon';
import {
    CombinedTagOption,
    IScheduledBulkMessage,
    SYSTEM_DATE_OPTIONS,
    recipientGroupLabels,
    schoolFilterLabels,
    entryStatusFilterLabels,
    tagLabels,
    hasTagFilters,
    idFilterLabels,
} from './EmailToolTypes';

interface IProps {
    onEditPending: (item: IScheduledBulkMessage) => void;
    recipientCategories: Selectable[];
    schoolOptions: Selectable[];
    entryStatusOptions: Selectable[];
    tagOptions: CombinedTagOption[];
    categoryOptions: Selectable[];
    gradeOptions: Selectable[];
    divisionOptions: Selectable[];
}

const ScheduledBulkEmailList = (props: IProps) => {
    const [scheduled, setScheduled] = useState<IScheduledBulkMessage[]>([]);

    useEffect(() => {
        loadScheduled();
    }, []);

    const loadScheduled = () => {
        store.server
            .getApi2<IScheduledBulkMessage[]>('../Email/PendingBulkMessages')
            .then((x: ServerResponse<IScheduledBulkMessage[]>) => {
                if (x.Success) {
                    setScheduled(x.Value ?? []);
                } else {
                    toast.error('Failed to load scheduled bulk emails');
                    console.error(x.Message);
                }
            })
            .catch((error) => {
                toast.error(`Error loading scheduled bulk emails: ${error.message}`);
                console.error(`Error loading scheduled bulk emails: ${error.message}`);
            });
    };

    const handleCancel = (item: IScheduledBulkMessage) => {
        if (!window.confirm(`Cancel the scheduled message "${item.Subject || 'Untitled Message'}"?`)) return;

        store.server
            .postApi<ServerResponse<boolean>>(`../Email/CancelScheduledBulkEmail/${item.PublicId}`, {})
            .then((x: ServerResponse<boolean>) => {
                if (x.Success) {
                    toast.success('Scheduled email canceled.');
                    setScheduled((prev) => prev.filter((s) => s.PublicId !== item.PublicId));
                } else {
                    toast.error(x.Message || 'Failed to cancel scheduled email');
                }
            })
            .catch((error) => {
                toast.error(`Error canceling scheduled email: ${error.message}`);
            });
    };

    const whenLabel = (item: IScheduledBulkMessage) => {
        if (item.ScheduleRelationship) {
            const dateLabel = SYSTEM_DATE_OPTIONS.find((x) => x.value === item.ScheduleRelationship.SystemDateKey)?.label ?? item.ScheduleRelationship.SystemDateKey;
            const offset = item.ScheduleRelationship.DayOffset;
            const direction = offset < 0 ? 'before' : 'after';
            const days = Math.abs(offset);
            const resolved = item.ResolvedPreviewAtUtc ? ` (currently resolves to ${FormatDate(item.ResolvedPreviewAtUtc, 'date-time')})` : '';
            return `${days} day(s) ${direction} ${dateLabel}${resolved}`;
        }
        if (item.ScheduledAtUtc) {
            return FormatDate(item.ScheduledAtUtc, 'date-time');
        }
        return 'As soon as possible';
    };

    return (
        <div>
            <div className="flex-between">
                <div>
                    <h4>Scheduled</h4>
                </div>
                <div>
                    <Button type="refresh" onClick={loadScheduled} text="Refresh" />
                </div>
            </div>
            <div className="item-list">
                {scheduled.map((item) => (
                    <div key={item.PublicId} className="list-item">
                        <h3>{item.Subject || 'Untitled Message'}</h3>
                        <div>
                            <span className={`label label-${item.Status === 'Processing' ? 'warning' : 'default'}`}>{item.Status}</span>
                        </div>
                        <div>
                            <Icon type={IconType.alarmClock} /> Scheduled for: {whenLabel(item)}
                        </div>
                        <div>
                            Created by: {item.SentBy || 'Unknown'} on {FormatDate(item.CreatedAtUtc, 'pretty')}
                        </div>
                        <div>
                            Sent to: {recipientGroupLabels(item.ComposeState, props.recipientCategories)}
                            {hasTagFilters(item.ComposeState) && <span title="Tag filters were applied to this message"> <Icon type={IconType.tags} /></span>}
                        </div>
                        {(item.ComposeState?.SchoolFilter?.length ?? 0) > 0 && (
                            <div>School Filter: {schoolFilterLabels(item.ComposeState, props.schoolOptions)}</div>
                        )}
                        {(item.ComposeState?.EntryStatusFilter?.length ?? 0) > 0 && (
                            <div>Entry Status Filter: {entryStatusFilterLabels(item.ComposeState, props.entryStatusOptions)}</div>
                        )}
                        {(item.ComposeState?.CategoryFilter?.length ?? 0) > 0 && (
                            <div>Category Filter: {idFilterLabels(item.ComposeState?.CategoryFilter, props.categoryOptions)}</div>
                        )}
                        {(item.ComposeState?.GradeFilter?.length ?? 0) > 0 && (
                            <div>Grade Filter: {idFilterLabels(item.ComposeState?.GradeFilter, props.gradeOptions)}</div>
                        )}
                        {(item.ComposeState?.DivisionFilter?.length ?? 0) > 0 && (
                            <div>Division Filter: {idFilterLabels(item.ComposeState?.DivisionFilter, props.divisionOptions)}</div>
                        )}
                        {(item.ComposeState?.Tags?.length ?? 0) > 0 && (
                            <div>Tag Filter ({item.ComposeState?.RequireAllTags ? 'Require All' : 'Require Any'}): {tagLabels(item.ComposeState?.Tags, props.tagOptions)}</div>
                        )}
                        {(item.ComposeState?.ExcludeTags?.length ?? 0) > 0 && (
                            <div>Exclude Tag Filter: {tagLabels(item.ComposeState?.ExcludeTags, props.tagOptions)}</div>
                        )}
                        {(item.Attachments?.length ?? 0) > 0 && (
                            <div>
                                Attachments: {item.Attachments.map((a) => (
                                    <span key={a.FileId}> <a href={a.Url} target="_blank" rel="noreferrer">{a.FileName}</a></span>
                                ))}
                            </div>
                        )}
                        <div>
                            <Button
                                type="edit"
                                onClick={() => props.onEditPending(item)}
                                text="Edit"
                                disabled={item.Status !== 'Pending'}
                            />
                            {' '}
                            <Button
                                type="cancel"
                                onClick={() => handleCancel(item)}
                                text="Cancel"
                                disabled={item.Status !== 'Pending'}
                            />
                        </div>
                    </div>
                ))}
                {scheduled.length === 0 && <p>No bulk emails are currently scheduled.</p>}
            </div>
        </div>
    );
};

export default ScheduledBulkEmailList;
